import { Box, Grid, Typography } from '@mui/material'
import { format } from 'date-fns'
import { useRouter } from 'next/router'

import { Article } from '@ecm/data/Article'
import Anchor from '@ecm/ui/element/Anchor'
import Color from '@ecm/ui/element/Color'
import ContentfulImage, { ImageType } from '@ecm/ui/element/ContentfulImage'

export const ArticleDefaultImageUrl = '/images/generic-blog-post-feature-image.jpg'

type ArticleCardProps = {
  article: Article
  priority?: boolean
}

const ArticleCard = ({ article, priority }: ArticleCardProps) => {
  const router = useRouter()

  const onCardClick = () => {
    if (article.redirectTo) {
      window.location.href = article.redirectTo
    } else {
      router.push(`/blog/${article.slug}`)
    }
  }

  const getArticleCardUrl = () => {
    if (article.redirectTo) {
      return article.redirectTo
    } else {
      return `/blog/${article.slug}`
    }
  }

  return (
    <Grid
      container
      direction="column"
      onClick={onCardClick}
      sx={{
        ':hover': { boxShadow: '0px 10px 25px 5px rgba(75, 79, 99, 0.1)' },
        background: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0px 10px 20px rgba(75, 79, 99, 0.07)',
        cursor: 'pointer',
        height: '100%',
        img: { borderRadius: '8px 8px 0 0' },
        width: '100%',
      }}
    >
      <Box sx={{ height: '185px', position: 'relative', width: '100%' }}>
        <ContentfulImage
          imageType={ImageType.BlogGrid}
          src={`https:${article.mainImageUrl || ArticleDefaultImageUrl}`}
          alt={article.title}
          layout="fill"
          objectFit="cover"
          priority={priority}
        />
      </Box>

      <Grid container py="16px" px={{ md: '24px', xs: '16px' }}>
        {article.categories.length > 0 && (
          <Anchor href={`/blog/category/${article.categories[0].slug}`}>
            <Typography
              variant="overline"
              component="h4"
              bgcolor={Color.smoke.lighter}
              padding="0.57em"
              color={Color.saint.darker}
              borderRadius="5px"
              fontSize="11px"
              sx={{ ':hover': { backgroundColor: Color.smoke.normal } }}
            >
              {article.categories[0].name}
            </Typography>
          </Anchor>
        )}
      </Grid>

      <Grid
        container
        flexDirection="column"
        flexGrow={1}
        justifyContent="space-between"
        pb="16px"
        px={{ md: '24px', xs: '16px' }}
      >
        <Box
          sx={{
            mb: { md: '3em', xs: '2em' },
            overflow: 'hidden',
          }}
        >
          <Anchor href={getArticleCardUrl()}>
            <Typography variant="h5" component="h3" mb="1em" color={Color.slate.normal}>
              {article.title}
            </Typography>
          </Anchor>

          <Typography variant="p3" color={Color.slate.lightest} lineHeight="28px" maxHeight="100px">
            {article.contentPreview}
          </Typography>
        </Box>
        <Grid container alignItems="center" justifyContent="space-between">
          <Anchor href={`/blog/author/${article.author.id}`}>
            <Typography
              variant="overline"
              component="h4"
              color={Color.slate.lightest}
              fontSize="11px"
              sx={{ ':hover': { color: Color.saint.normal } }}
            >
              By {article.author.fullname}
            </Typography>
          </Anchor>

          <Typography variant="overline" color={Color.slate.lightest} fontSize="11px">
            {format(new Date(article.publishedDate), 'MMMM d, yyyy')}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ArticleCard
